import { ChangeEvent, useCallback, useContext } from 'react'
import _ from 'lodash'
import styled from '@emotion/styled'
import { L1ReasonableButton } from '../l1/L1Button'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import L1Select from '../l1/L1Select'
import { AssertionStatus, DiagnosisPlan, DiagnosisReviewItem, DiagnosisReviewItemPresence, diagnosisReviewItemIsPendingAuditComment, diagnosisReviewItemPresence, diagnosisReviewItemRequiresCarePlan, diagnosisReviewItemShouldShowCommentsToClinician, getCarePlanDisplayValue, getStatusDisplayValue, IndexedModel, PrescriptionReviewConcept } from '@fusion-advantage/formlib'
import { L2TabularListContent, L2TabularListHeader, L2TabularListRoot } from '../l2/L2TabularList'
import { FusionColor } from '../../../theme/FusionStyle'
import { Theme, useTheme } from '@emotion/react'
import { useFormSelector } from '../../../redux/formStoreHooks'
import L6FormContext from '../l6/L6FormContext'
import { L1DiagnosisReviewRowEvidence, L1DiagnosisReviewRowSource } from '../l1/L1DiagnosisReview'
import { selectExport } from '../../../redux/selectForm'

export const DiagnosisReviewSelect = styled(L1Select)(({theme}) => ({
	padding: theme.spacing(1.5, 5, 1.5, 1.5),
}))

const DiagnosisReviewRowRoot = styled.div(({theme}) => ({
	gridColumn: 'rowstartln / rowendln',
	display: 'grid',
	gridTemplateColumns: 'subgrid',
	border: `1px solid ${theme.colors.lines}`,
	fontSize: 16,
	'> *': {
		padding: theme.spacing(1.5, 2),
	},
	breakInside: 'avoid',
}))

const DiagnosisWrapper = styled.div(({theme}) => ({
	alignSelf: 'center',
	fontSize: 16,
	fontWeight: 600,
	// prefer not to break in the middle of a word,
	// but do it to avoid breaking the grid contraints
	overflowWrap: 'break-word',
	minWidth: 0,
}))

const SelectWrapper = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'flex-start',
}))

const AuditResponseWrapper = styled.div(({theme}) => ({
	alignSelf: 'stretch',
	alignContent: 'center',
	backgroundColor: theme.colors[FusionColor.Second],
	'> button': {
		flexGrow: 1,
		textAlign: 'center'
	} 
}))

const StaticLabelWrapper = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	fontSize: 16,
	fontWeight: 400,
	justifyContent: 'center',
	alignItems: 'flex-start'
}))

const ComorbidityStatusWrapper = styled.div({
	alignSelf: 'center',
	// prefer not to break in the middle of a word,
	// but do it to avoid breaking the grid contraints
	overflowWrap: 'break-word',
	minWidth: 0,
})

type DiagnosisReviewRowProps = {
	model: DiagnosisReviewItem,
	isQueryReview: boolean,
	onChange: (updatedModel: DiagnosisReviewItem) => void,
	onAuditCommentClick: () => void,
}

const DiagnosisReviewRow = ({model, isQueryReview, onChange, onAuditCommentClick}: DiagnosisReviewRowProps) => {
	const theme = useTheme()
	const encounterAppLastStatusDT = useFormSelector(state => selectExport(state)?.Form.Encounter.EncounterAppLastStatusDT)
	const { jumpToConcept } = useContext(L6FormContext)

	const handleAssertionStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
		const updatedModel = _.cloneDeep(model)
		updatedModel.status = null
		if(e.target.value !== '') {
			updatedModel.status = e.target.value as AssertionStatus
		}
		onChange(updatedModel)
	}

	const handleDxEvidenceClick = useCallback((e: React.MouseEvent) => {
		e.preventDefault()
		if(model.cid) {
			jumpToConcept(model.cid)
		}
	}, [jumpToConcept, model])

	const handleRxEvidenceClick = useCallback((e: React.MouseEvent) => {
		jumpToConcept(PrescriptionReviewConcept.cid)
	}, [jumpToConcept])

	const handleCarePlanChange = (e: ChangeEvent<HTMLSelectElement>) => {
		const updatedModel = _.cloneDeep(model)
		updatedModel.carePlan = null
		if(e.target.value !== '') {
			updatedModel.carePlan = e.target.value as DiagnosisPlan
		}
		onChange(updatedModel)
	}

	const pendingAuditComment = diagnosisReviewItemIsPendingAuditComment(model, encounterAppLastStatusDT!)

	return (
		<DiagnosisReviewRowRoot>
			<L1DiagnosisReviewRowSource model={model} />
			<DiagnosisWrapper>{model.title}</DiagnosisWrapper>
			<ComorbidityStatusWrapper>{model.icdComorbidityStatus}</ComorbidityStatusWrapper>
			{
				diagnosisReviewItemPresence(model) === DiagnosisReviewItemPresence.Present ? (
					<SelectWrapper>
						<DiagnosisReviewSelect
							nullOption={true}
							value={model.status as string}
							onChange={handleAssertionStatusChange}
						>
							<option value={AssertionStatus.Asserted}>{getStatusDisplayValue(AssertionStatus.Asserted)}</option>
							<option value={AssertionStatus.HistoryOf}>{getStatusDisplayValue(AssertionStatus.HistoryOf)}</option>
							<option value={AssertionStatus.Indeterminate}>{getStatusDisplayValue(AssertionStatus.Indeterminate)}</option>
						</DiagnosisReviewSelect>
					</SelectWrapper>
				) : (
					<StaticLabelWrapper>{getStatusDisplayValue(model.status)}</StaticLabelWrapper>
				)
			}
			<L1DiagnosisReviewRowEvidence
				model={model}
				onDxEvidenceClick={handleDxEvidenceClick}
				onRxEvidenceClick={handleRxEvidenceClick}
			/>
			<SelectWrapper>
				{
					diagnosisReviewItemRequiresCarePlan(model) ? (
						<DiagnosisReviewSelect
							nullOption={true}
							value={model.carePlan}
							onChange={handleCarePlanChange}
						>
							<option value={DiagnosisPlan.Continue}>Continue current care plan</option>
							<option value={DiagnosisPlan.PCP}>PCP appointment recommended</option>
							<option value={DiagnosisPlan.Urgent}>Urgent issue</option>
						</DiagnosisReviewSelect>
					) : (
						<StaticLabelWrapper>
							{/* this shouldn't ever display anything, but might help us catch a future logic error */}
							{getCarePlanDisplayValue(model.carePlan)}
						</StaticLabelWrapper>
					)
				}	
			</SelectWrapper>
			{
				isQueryReview ? (
					<AuditResponseWrapper>
						{
							diagnosisReviewItemShouldShowCommentsToClinician(model) && (
								<L1ReasonableButton
									onClick={onAuditCommentClick}
									icon={!pendingAuditComment ? faCheck : undefined}
									label={!pendingAuditComment ? 'Reviewed' : 'Review'}
									color={!pendingAuditComment ? FusionColor.BlueMain : FusionColor.White}
									wide={true}
								/>
							)
						}
					</AuditResponseWrapper>
				) : null
			}
		</DiagnosisReviewRowRoot>
	)
}

interface L3DiagnosisReviewProps {
	isQueryReview: boolean,
	models: IndexedModel<DiagnosisReviewItem>[],
	onUpdateDiagnosis: (modelIdx: number, updatedItem: DiagnosisReviewItem) => void,
	onAuditCommentClick: (modelIdx: number) => void,
}

const L3DiagnosisReview = ({
	isQueryReview,
	models,
	onUpdateDiagnosis,
	onAuditCommentClick,
}: L3DiagnosisReviewProps) => {
	const theme = useTheme()

	const handleDiagnosisChange = (modelIdx: number) => (updatedModel: DiagnosisReviewItem) => {
		onUpdateDiagnosis(modelIdx, updatedModel)
	}

	const handleAuditCommentClick = (modelIdx: number) => () => {
		onAuditCommentClick(modelIdx)
	}

	const items = models.map(({model, stateIdx}) => (
		<DiagnosisReviewRow
			key={stateIdx}
			model={model}
			isQueryReview={isQueryReview}
			onChange={handleDiagnosisChange(stateIdx)}
			onAuditCommentClick={handleAuditCommentClick(stateIdx)}
		/>
	))

	// constrain dan's column in case he rambles!
	const dansComorbidString = 'minmax(auto, 8rem)'
	const gridTemplate = ['max-content', '1fr', dansComorbidString, 'max-content', 'max-content', 'max-content']
	if(isQueryReview) {
		gridTemplate.push('max-content')
	}

	return (
		<L2TabularListRoot columnCount={gridTemplate.length} gridTemplateColumns={gridTemplate.join(' ')}>
			<L2TabularListHeader>
				<div>Source</div>
				<div>Diagnosis</div>
				<div>Comorbid</div>
				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
				}}>
					Status
				</div>
				<div>Evidence</div>
				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
				}}>
					Care Plan Overview
				</div>
				{
					isQueryReview ? (
						<div style={{ 
							backgroundColor: theme.colors[FusionColor.Third], 
							textAlign: 'center', 
							borderTopRightRadius: theme.spacing(1),
							borderTopLeftRadius: theme.spacing(1),
						}}>
							Audit Query
						</div>
					) : null
				}
			</L2TabularListHeader>
			<L2TabularListContent>
				{items}
			</L2TabularListContent>
		</L2TabularListRoot>
	)
}

export default L3DiagnosisReview
